import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const Recognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

// Browser speech-to-text; calls onText(finalChunk) for each finished phrase.
export function useVoiceInput(onText) {
  const [listening, setListening] = useState(false);
  const rec = useRef(null);
  const cb = useRef(onText);
  cb.current = onText;

  useEffect(() => () => rec.current?.abort(), []);

  const stop = useCallback(() => { rec.current?.stop(); setListening(false); }, []);

  const start = useCallback(() => {
    if (!Recognition) {
      toast.error("Voice typing isn't supported in this browser — try Chrome or Safari");
      return;
    }
    const r = new Recognition();
    r.lang = navigator.language || "en-US";
    r.continuous = true;
    r.interimResults = false;
    r.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        if (e.results[i].isFinal) cb.current(e.results[i][0].transcript.trim());
      }
    };
    r.onerror = (e) => {
      if (e.error === "not-allowed") toast.error("Allow microphone access to use voice typing");
      else if (e.error !== "aborted" && e.error !== "no-speech") toast.error("Voice typing stopped — please try again");
      setListening(false);
    };
    r.onend = () => setListening(false);
    rec.current = r;
    r.start();
    setListening(true);
  }, []);

  return { listening, toggle: listening ? stop : start, stop, supported: !!Recognition };
}
